import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { ErrorMessage } from '@hookform/error-message';
import { Row, Form, Col, Input } from 'reactstrap';
import { toast } from 'react-toastify';

import ReactButton from 'components/Buttons/ReactButton';
import Loader from 'components/Loader';
import CustomFieldsInputsForm from '../../../shared/components/CustomFieldsInputsForm';

import ProductService from '../../services/productService';

import { useEntityCustomFieldsByRecordType } from 'views/fieldConfigs/hooks/useCustomFieldsByRecordType';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import { maskPhoneNumber } from 'views/CRM/shared/utils/phoneNumberHelpers';
import { formatCustomFieldsToDisplay } from 'views/CRM/shared/helpers/customFieldsHelpers';
import { formatCustomFieldsToSubmit } from 'views/CRM/shared/helpers/customFieldsHelpers';
import { ScreenPrompt } from 'components/ScreenPrompt';
import CurrencyMask from 'components/Inputs/CurrencyMask';
import { parseIntFromString } from 'utils/fuctions/parseIntFromString';
import { verifyRoles } from 'components/Authentication/VerifyRoles';
import { removeTelephoneAuxiliaryFields } from 'views/CRM/shared/helpers/customFieldsHelpers';

const ProductInputs = ({
  recordTypeId,
  product,
  showCallIconOnPhoneField = false,
  showMessageIconOnPhoneField = false,
  reloadProducts = () => {}
}) => {
  const history = useHistory();
  const [isSubmiting, setIsSubmiting] = useState(false);
  const { getCustomFields, customFields, isLoadingCustomFields } =
    useEntityCustomFieldsByRecordType(entitiesName.PRODUCT, recordTypeId);

  const { control, handleSubmit, errors, setValue, reset, formState, watch } =
    useForm();

  const { isDirty } = formState;

  useEffect(() => {
    if (product) {
      const formattedProduct = formatCustomFieldsToDisplay(
        {
          ...product,
          phone: maskPhoneNumber(product.phone)
        },
        customFields
      );

      reset(formattedProduct);
    }
  }, [reset, product, customFields]);

  useEffect(() => {
    getCustomFields();
  }, [getCustomFields]);

  const onSubmit = async (data) => {
    setIsSubmiting(true);

    let dataDTO = formatCustomFieldsToSubmit(
      removeTelephoneAuxiliaryFields(data, customFields),
      customFields
    );

    if (dataDTO.basePrice < 0) throw new Error('O preço base é inválido');
    dataDTO.basePrice = parseIntFromString(dataDTO.basePrice) / 100;

    const saveProductPromise = product
      ? ProductService.update(product.id, dataDTO)
      : ProductService.create({ ...dataDTO, recordTypeId });

    try {
      const response = await saveProductPromise;
      toast.success(`Produto ${product ? 'atualizado' : 'criado'} com sucesso`);
      reloadProducts();

      if (verifyRoles(['CRM_PRODUCTS_EDIT'])) {
        history.push(
          `/admin/crm/product-details/${recordTypeId}/${
            response?.data?.data?.id || product.id
          }`
        );
      } else if (!verifyRoles(['CRM_PRODUCTS_EDIT'])) {
        history.push(`/admin/crm/products`);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          `Erro ao ${product ? 'atualizar' : 'criar'} produto`
      );
    } finally {
      setIsSubmiting(false);
    }
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  const cancelAction = () => {
    history.push('/admin/crm/products', { recordTypeId });
  };

  if (isLoadingCustomFields) {
    return (
      <Row className="justify-content-md-center">
        <Loader />
      </Row>
    );
  }

  return (
    <>
      <ScreenPrompt
        when={isDirty && !formState.isSubmitting}
        message={`Está certo de que deseja sair? Terá que preencher os dados novamente quando retornar`}
      />
      <Form className="needs-validation" onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col sm="6" className="mb-3">
            <label className="form-control-label text-capitalize">Nome*</label>
            <Controller
              render={(props) => (
                <Input
                  value={props.value}
                  onChange={props.onChange}
                  type="text"
                  placeholder="Nome"
                />
              )}
              control={control}
              defaultValue=""
              rules={{ required: 'Campo obrigatório' }}
              name="name"
            />
            <ErrorMessage
              errors={errors}
              name="name"
              render={({ message }) => errorFormMessage(message)}
            />
          </Col>
          <Col sm="6" className="mb-3">
            <label className="form-control-label text-capitalize">
              Descrição
            </label>
            <Controller
              render={(props) => (
                <Input
                  value={props.value}
                  onChange={props.onChange}
                  type="text"
                  placeholder="Descrição"
                />
              )}
              control={control}
              defaultValue=""
              rules={{ maxLength: 255 }}
              name="description"
            />
            <ErrorMessage
              errors={errors}
              name="description"
              render={({ message }) => errorFormMessage(message)}
            />
          </Col>
        </Row>

        <Row>
          <Col sm="4" className="mb-3">
            <label
              className="form-control-label text-capitalize"
              htmlFor="basePrice"
            >
              Preço Base
            </label>
            <Controller
              render={(props) => (
                <CurrencyMask
                  valueController={setValue}
                  value={props.value}
                  fieldName="basePrice"
                />
              )}
              control={control}
              defaultValue=""
              name="basePrice"
            />
            <ErrorMessage
              errors={errors}
              name="basePrice"
              render={({ message }) => errorFormMessage(message)}
            />
          </Col>
          <Col sm="4" className="mb-3">
            <label className="form-control-label text-capitalize">
              Código*
            </label>
            <Controller
              render={(props) => (
                <Input
                  value={props.value}
                  onChange={props.onChange}
                  type="text"
                  placeholder="Código do Produto"
                />
              )}
              control={control}
              defaultValue=""
              rules={{ required: 'Campo obrigatório' }}
              name="code"
            />
            <ErrorMessage
              errors={errors}
              name="code"
              render={({ message }) => errorFormMessage(message)}
            />
          </Col>
          <Col sm="4" className="mb-3">
            <label className="form-control-label text-capitalize">
              ID Externo
            </label>
            <Controller
              render={(props) => (
                <Input
                  value={props.value}
                  onChange={props.onChange}
                  type="text"
                  placeholder="ID Externo"
                />
              )}
              control={control}
              defaultValue=""
              rules={{ maxLength: 255 }}
              name="externalId"
            />
            <ErrorMessage
              errors={errors}
              name="externalId"
              render={({ message }) => errorFormMessage(message)}
            />
          </Col>
        </Row>

        <Row className="mb-1">
          <CustomFieldsInputsForm
            fields={customFields}
            control={control}
            errors={errors}
            setValue={setValue}
            Controller={Controller}
            options={{ showCallIconOnPhoneField, showMessageIconOnPhoneField }}
            contactMessageClientName={product?.name}
            watch={watch}
            isOnEditPage={!!product}
          />
        </Row>

        <ReactButton
          btnColor="confirmation"
          type="submit"
          disabled={isSubmiting}
        >
          Salvar
        </ReactButton>

        {!product && (
          <ReactButton
            btnColor="cancelation"
            onClick={() => {
              return cancelAction();
            }}
          >
            Cancelar
          </ReactButton>
        )}
      </Form>
    </>
  );
};

export default ProductInputs;
